import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  formatBasisPointsPercent,
  formatPeriod,
  isPeriodStart,
  nzDate,
  periodStartFor,
  previousPeriodStart,
} from '@promanage/core';
import { Button, Field, Panel, PanelHeader, Select } from '@promanage/ui';
import { LoadFailed, QueryLoading } from '../components/QueryState';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { recentMonths } from '../features/reports/months';
import { StatTile } from '../features/dashboard/StatTile';
import { trpc, type RouterOutputs } from '../lib/trpc';

/** Shape-checked only, like the reconciliation report's — see `reports.reconciliation.tsx`. */
function parseSearch(search: Record<string, unknown>): { period?: string } {
  return typeof search.period === 'string' && isPeriodStart(search.period)
    ? { period: search.period }
    : {};
}

export const Route = createFileRoute('/reports/matching')({
  validateSearch: parseSearch,
  component: MatchRatePage,
});

type MatchRate = RouterOutputs['reports']['matchRate'];

const MONTHS_OFFERED = 24;

/**
 * The auto-match rate — how much of a month's statement nobody had to decide.
 *
 * THE HEADLINE IS LABOUR, NOT ACCURACY. A proposal a person clicked "accept" on was right, and it
 * still cost a keystroke; that is what an agency is buying its way out of, so it is counted apart
 * from what the matcher did alone. The three tiles beside the headline are where the remaining
 * work went, which is what tells somebody what to change: a pile of accepted proposals is a
 * threshold question, a pile chosen by hand is a matcher question.
 *
 * THE MATCHER'S MISTAKES ARE ON THE PAGE. An automatic match that was undone reads afterwards as
 * whatever it was re-matched as, so without "Taken back" the rate could only ever flatter the
 * matcher.
 */
function MatchRatePage() {
  const selection = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const today = nzDate(new Date());
  // Last month by default, like the reconciliation: this month's statement is still arriving.
  const period = selection.period ?? previousPeriodStart(periodStartFor(today));
  const months = recentMonths(periodStartFor(today), MONTHS_OFFERED);

  const query = useQuery(
    trpc.reports.matchRate.queryOptions(
      { period },
      { meta: { errorTitle: 'Could not work out the match rate' }, staleTime: 30_000 },
    ),
  );
  const data = query.data;

  return (
    <ReportWorkspace
      title="Auto-match rate"
      subtitle={`${formatPeriod(period)} — statement lines matched without anybody deciding`}
      actions={
        <Button size="sm" disabled={data === undefined} onClick={() => window.print()}>
          Print
        </Button>
      }
      controls={
        <Panel className="print:hidden">
          <PanelHeader
            title="Report selection"
            subtitle="One month of statement lines, by the date the bank gave them."
          />
          <div className="px-5 pb-5">
            <Field label="Month" className="max-w-64">
              {(props) => (
                <Select
                  {...props}
                  value={period}
                  onChange={(event) => {
                    void navigate({ search: { period: event.target.value }, replace: true });
                  }}
                >
                  {months.map((month) => (
                    <option key={month} value={month}>
                      {formatPeriod(month)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </Panel>
      }
    >
      {query.isError ? (
        <LoadFailed retry={() => void query.refetch()} retrying={query.isFetching} />
      ) : query.isPending ? (
        <QueryLoading label="Counting statement lines..." />
      ) : data === undefined ? null : data.lines === 0 ? (
        <Panel className="p-6">
          <h2 className="text-[1.125rem] font-bold leading-tight tracking-[-0.014em]">
            No statement lines in {formatPeriod(period)}
          </h2>
          <p className="pt-1 text-[0.8125rem] text-ink-muted">
            Import the trust account&rsquo;s statement for the month and the rate is worked out from
            it.
          </p>
          <div className="pt-4">
            <Link to="/reconcile/import">
              <Button size="sm">Import a statement</Button>
            </Link>
          </div>
        </Panel>
      ) : (
        <MatchRateReport data={data} />
      )}
    </ReportWorkspace>
  );
}

function MatchRateReport({ data }: { data: MatchRate }) {
  const target = formatBasisPointsPercent(data.targetBp);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Matched automatically"
          value={formatBasisPointsPercent(data.autoRateBp)}
          /*
           * Green for on track, `warn` below it — never red. Falling short of a target is a thing
           * to work on, and red is kept for error and destruction (DESIGN_SYSTEM §3).
           */
          tone={data.meetsTarget === null ? 'neutral' : data.meetsTarget ? 'positive' : 'warn'}
          {...(data.meetsTarget === null
            ? {}
            : {
                chip: {
                  text: `Target ${target}`,
                  tone: data.meetsTarget ? ('positive' as const) : ('warn' as const),
                },
              })}
          detail={`${String(data.auto)} of ${String(data.measured)} lines`}
        />
        <StatTile
          label="Accepted proposals"
          value={data.accepted}
          detail="The matcher proposed; a person agreed"
        />
        <StatTile label="Chosen by hand" value={data.manual} detail="A person picked the target" />
        <StatTile
          label="Still unmatched"
          value={data.unmatched}
          tone={data.unmatched > 0 ? 'warn' : 'neutral'}
          to="/reconcile/queue"
          detail={
            data.staged > 0
              ? `${String(data.staged)} more decided and waiting for Process`
              : 'Nothing waiting in a batch'
          }
        />
      </div>

      {data.unrecorded > 0 ? (
        <Panel className="border-l-4 border-l-warn p-5">
          <p className="text-[0.8125rem] leading-relaxed">
            <span className="font-bold">
              {data.unrecorded} {data.unrecorded === 1 ? 'line was' : 'lines were'} matched before
              ProManage recorded who decided.
            </span>{' '}
            They are left out of the rate rather than guessed at. Months from here on are measured
            in full.
          </p>
        </Panel>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel>
          <PanelHeader
            title="Money in and money out"
            subtitle="Receipts are matched to tenancies; payments to what was keyed by hand."
          />
          <table className="w-full border-collapse text-[0.8125rem]">
            <thead>
              <tr className="border-t border-rule text-left text-[0.6875rem] tracking-wide text-ink-subtle uppercase">
                <th className="px-5 py-2 font-semibold">Direction</th>
                <th className="px-5 py-2 text-right font-semibold">Lines</th>
                <th className="px-5 py-2 text-right font-semibold">Automatic</th>
                <th className="px-5 py-2 text-right font-semibold">Rate</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {(
                [
                  ['Money in', data.byDirection.in],
                  ['Money out', data.byDirection.out],
                ] as const
              ).map(([label, row]) => (
                <tr key={label} className="border-t border-rule">
                  <td className="px-5 py-3 font-semibold">{label}</td>
                  <td className="px-5 py-3 text-right">{row.lines}</td>
                  <td className="px-5 py-3 text-right">{row.auto}</td>
                  <td className="px-5 py-3 text-right">
                    {formatBasisPointsPercent(row.autoRateBp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel>
          <PanelHeader
            title="Taken back"
            subtitle="Matches somebody undid — how often the matcher was wrong."
          />
          <div className="px-5 pb-5 text-[0.8125rem]">
            {data.autoDecisions === 0 ? (
              <p className="text-ink-muted">The matcher made no automatic matches this month.</p>
            ) : (
              <p>
                <span className="text-[1.5rem] font-bold tabular-nums tracking-[-0.02em]">
                  {formatBasisPointsPercent(data.autoWithdrawnRateBp)}
                </span>
                <span className="block pt-1 text-ink-muted">
                  {data.withdrawn.auto} of the {data.autoDecisions} automatic matches were undone
                  {data.withdrawn.accepted + data.withdrawn.manual > 0
                    ? `, and ${String(data.withdrawn.accepted + data.withdrawn.manual)} decisions made by people`
                    : ''}
                  .
                </span>
              </p>
            )}
          </div>
        </Panel>
      </div>

      <Panel>
        <PanelHeader
          title="By matching rule"
          subtitle="Which rung of the cascade decided each line, most certain first."
        />
        {data.byStrategy.length === 0 ? (
          <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">
            Nothing was matched this month.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] border-collapse text-[0.8125rem]">
              <thead>
                <tr className="border-t border-rule text-left text-[0.6875rem] tracking-wide text-ink-subtle uppercase">
                  <th className="px-5 py-2 font-semibold">Rule</th>
                  <th className="px-5 py-2 text-right font-semibold">Lines</th>
                  <th className="px-5 py-2 text-right font-semibold">Automatic</th>
                  <th className="px-5 py-2 text-right font-semibold">Accepted</th>
                  <th className="px-5 py-2 text-right font-semibold">Taken back</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {data.byStrategy.map((rung) => (
                  <tr key={rung.strategy} className="border-t border-rule">
                    <td className="px-5 py-3 font-semibold">{rung.label}</td>
                    <td className="px-5 py-3 text-right">{rung.lines}</td>
                    <td className="px-5 py-3 text-right">{rung.auto}</td>
                    <td className="px-5 py-3 text-right">{rung.accepted}</td>
                    <td
                      className={`px-5 py-3 text-right ${rung.withdrawnAuto > 0 ? 'text-warn' : ''}`}
                    >
                      {rung.withdrawn}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
